/**
 * <ActivityMediaList> — les pièces d'une activité, en lecture seule (organism).
 *
 * Le parent regarde, il ne retire rien : ce n'est donc pas `<ImageAttachment>` / `<PDFAttachment>`
 * / `<LinkAttachment>`, qui sont les aperçus du sélecteur et exigent tous un `onDelete`. Les
 * images passent en vignettes (`<MediaThumbnail>` sans poubelle), le PDF et le lien en ligne de
 * carte-liste.
 *
 * Toucher une pièce l'ouvre en grand — `<MediaDisplay>` route par `kind`, comme dans
 * `<MediaPicker>`. Quelle pièce est ouverte est un état d'INTERFACE : il vit ici.
 *
 * En `mediaLayout="full"`, un PDF se lit aussi dans la page, sous la liste des pièces, comme une image
 * en grand (`<PdfInline>`, au web ; le natif garde la ligne seule).
 */
import React, { useState } from "react"
import { Pressable } from "react-native"
import { StyleSheet } from "react-native-unistyles"

import { type UIActivityMedia } from "../../../ports/primary/ui/types"
import { FittedImage } from "../../atoms/FittedImage"
import { HorizontalList } from "../../atoms/HorizontalList"
import { MediaThumbnail } from "../../atoms/MediaThumbnail"
import { Row } from "../../atoms/containers/Row"
import { Stack } from "../../atoms/containers/Stack"
import { Icon } from "../../atoms/icons/Icon"
import { Text } from "../../atoms/typography/Text"
import { CardList, CardListRow } from "../../molecules/CardList"
import { PdfInline } from "../../molecules/PdfDisplay"
import { MediaDisplay } from "../MediaDisplay"

/** Ce que le visualiseur montre en ce moment. Rien d'autre n'est un état ici. */
type Viewer = { kind: "image"; index: number } | { kind: "pdf"; uri: string } | { kind: "link"; url: string }

/** Les pièces d'une activité : les images en vignettes, ou chacune à toute la largeur — et au web, les pages des PDF. */
export type ActivityMediaLayout = "thumbnails" | "full"

export type ActivityMediaListProps = {
    media: UIActivityMedia[]
    /** Taille d'une vignette — 96 par défaut, 140 quand il n'y a qu'une image. */
    thumbnailSize?: number
    /** `contain` montre le document entier au lieu de le recadrer. Défaut `cover`. */
    fit?: "cover" | "contain"
    /**
     * `thumbnails` (défaut) : des vignettes en rangée, celles de la fiche du parent. `full` : chaque
     * image à toute la largeur, à sa proportion, l'une sous l'autre : l'élève qui fait l'exercice
     * le lit en grand ; au web, les pages d'un PDF aussi, sous la liste des pièces (`<PdfInline>`).
     */
    mediaLayout?: ActivityMediaLayout
    /** Un PDF de la liste ne s'est pas chargé (dans la page ou dans la visionneuse). Absent : rien. */
    onPdfError?: (cause: unknown) => void
    testID?: string
}

export function ActivityMediaList(props: ActivityMediaListProps) {
    const { media, thumbnailSize, fit = "cover", mediaLayout = "thumbnails", onPdfError, testID } = props

    const [viewer, setViewer] = useState<Viewer | null>(null)

    const images = media.filter((m): m is Extract<UIActivityMedia, { kind: "image" }> => m.kind === "image")
    const files = media.filter((m) => m.kind !== "image")
    const pdfs = files.filter((m): m is Extract<UIActivityMedia, { kind: "pdf" }> => m.kind === "pdf")

    if (media.length === 0) return null

    const size = thumbnailSize ?? (images.length === 1 ? 140 : 96)

    return (
        <Stack gap="3" testID={testID}>
            {images.length > 0 &&
                mediaLayout === "full" &&
                images.map((image, index) => (
                    <FittedImage
                        key={`${image.uri}-${index}`}
                        uri={image.uri}
                        onPress={() => setViewer({ kind: "image", index })}
                        accessibilityLabel={`Pièce jointe ${index + 1}`}
                    />
                ))}
            {images.length > 0 && mediaLayout === "thumbnails" && (
                <HorizontalList gap="2">
                    {images.map((image, index) => (
                        <MediaThumbnail
                            key={`${image.uri}-${index}`}
                            uri={image.uri}
                            size={size}
                            fit={fit}
                            onPress={() => setViewer({ kind: "image", index })}
                            accessibilityLabel={`Pièce jointe ${index + 1}`}
                        />
                    ))}
                </HorizontalList>
            )}

            {files.length > 0 && (
                <CardList>
                    {files.map((file, index) => (
                        <Pressable
                            key={`${file.kind}-${index}`}
                            accessibilityRole="button"
                            onPress={() =>
                                setViewer(
                                    file.kind === "pdf"
                                        ? { kind: "pdf", uri: file.uri }
                                        : { kind: "link", url: file.url }
                                )
                            }
                        >
                            <CardListRow>
                                <Row gap="3" align="center">
                                    <Icon name={file.kind === "pdf" ? "file-pdf" : "link"} color="primary" />
                                    <Text numberOfLines={1} style={styles.grow}>
                                        {file.kind === "pdf" ? (file.name ?? "Document PDF") : (file.title ?? file.url)}
                                    </Text>
                                    <Icon name="chevron-right" size={16} color="text-muted" />
                                </Row>
                            </CardListRow>
                        </Pressable>
                    ))}
                </CardList>
            )}

            {mediaLayout === "full" &&
                pdfs.map((pdf, index) => (
                    <PdfInline
                        key={`${pdf.uri}-${index}`}
                        uri={pdf.uri}
                        onOpen={() => setViewer({ kind: "pdf", uri: pdf.uri })}
                        onError={onPdfError}
                    />
                ))}

            {viewer?.kind === "image" && (
                <MediaDisplay
                    visible
                    kind="image"
                    images={images.map((i) => ({ uri: i.uri }))}
                    initialIndex={viewer.index}
                    onClose={() => setViewer(null)}
                />
            )}
            {viewer?.kind === "pdf" && (
                <MediaDisplay
                    visible
                    kind="pdf"
                    uri={viewer.uri}
                    onClose={() => setViewer(null)}
                    onError={onPdfError}
                />
            )}
            {viewer?.kind === "link" && (
                <MediaDisplay visible kind="link" url={viewer.url} onClose={() => setViewer(null)} />
            )}
        </Stack>
    )
}

const styles = StyleSheet.create(() => ({
    grow: { flexShrink: 1, flexGrow: 1 },
}))
